import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FrequencyPicker from "@/components/FrequencyPicker";
import BuyBBMOnboardingModal from "@/components/BuyBBMOnboardingModal";
import { Play, Pause, Radio, Clock, HelpCircle } from "lucide-react";
import { artistSlug } from "@/lib/artistPages";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Default synopses for the current Deep Corners blocks — used only when admin hasn't set one.
const DEFAULT_SYNOPSIS = {
  "wake up call": "Bright openers and top-40 anthems to shake off Monday morning.",
  "morning chill": "Downtempo warmups — glassy synths, laid-back grooves, easy tempos.",
  "afternoon anime": "OSTs, city-pop, and J-electronic cuts from Tokyo bedrooms and beyond.",
  "hip hop hype": "Peak-hour bars, boom-bap flips, and 181-adjacent bangers.",
  "r&b forever": "Slow-burn soul, neo-R&B, and modern love-lock sessions.",
  "deep house hunting": "Late-night 4/4 grooves — dusty basslines and hypnotic loops.",
  "afrochill": "Afro-house, Amapiano and cinematic percussion into sunset.",
  "talk night cap": "Late-night talk, interviews, and slow-burn closers to wind the day down.",
};

const synopsisFor = (block, idx) => {
  if (block?.synopsis && block.synopsis.trim().length > 0) return block.synopsis.trim();
  const key = (block?.name || "").trim().toLowerCase();
  if (key && DEFAULT_SYNOPSIS[key]) return DEFAULT_SYNOPSIS[key];
  return `Two-hour rotation — Block ${idx + 1}.`;
};

const BlocksBanner = ({ deepCorners }) => {
  const rawBlocks = Array.isArray(deepCorners?.blocks) ? deepCorners.blocks : [];
  const blocks = Array.from({ length: 8 }, (_, i) => {
    const r = rawBlocks[i];
    if (Array.isArray(r)) return { name: "", synopsis: "" };
    if (r && typeof r === "object") return { name: r.name || "", synopsis: r.synopsis || "" };
    return { name: "", synopsis: "" };
  });

  return (
    <div
      className="mt-8 grid grid-cols-1 gap-px bg-white/5 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8"
      data-testid="schedule-blocks-banner"
    >
      {blocks.map((b, i) => {
        const startHour = 7 + i * 2;
        const endHour = startHour + 2;
        return (
          <div
            key={i}
            data-testid={`schedule-block-${i}`}
            className="flex flex-col gap-2 bg-black/60 p-4 backdrop-blur-sm"
          >
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                Block {i + 1}
              </span>
              <span className="font-mono text-xs font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">
                {String(startHour).padStart(2, "0")}–{String(endHour).padStart(2, "0")} ET
              </span>
            </div>
            <div className="font-[Unbounded,sans-serif] text-base font-bold leading-tight text-[#FAFAFA]">
              {b.name || `Block ${i + 1}`}
            </div>
            <p className="font-mono text-sm font-bold leading-snug text-[#A1A1AA]">
              {synopsisFor(b, i)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const Home = () => {
  const [shows, setShows] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [settings, setSettings] = useState({});
  const [bbmMc, setBbmMc] = useState(null);
  const [howToOpen, setHowToOpen] = useState(false);
  const { playing, toggle, nowPlaying } = usePlayer();

  useEffect(() => {
    api.get("/shows").then((r) => setShows(r.data)).catch(() => {});
    api.get("/tracks?featured=true").then((r) => setTracks(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api
        .get("/crypto/market-cap")
        .then((r) => { if (!cancelled) setBbmMc(r.data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const showsByDay = DAYS.reduce((acc, d) => {
    acc[d] = shows
      .filter((s) => s.day === d && s.dj === "Healing Frequencies")
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
    return acc;
  }, {});

  return (
    <div className="min-h-screen text-[#FAFAFA]" data-testid="home-root">
      <Navbar />

      {/* Hero / Now Playing */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-4 py-16 sm:px-6 md:grid-cols-12 md:py-24">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FF3B30] opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF3B30]"></span>
              </span>
              <span className="font-mono text-sm font-bold uppercase tracking-[0.28em] text-[#FF3B30]">Now Broadcasting</span>
              <span className="font-mono text-base font-bold uppercase tracking-[0.28em] text-[#A855F7]">// {nowPlaying.show_title || "Black Box Live"}</span>
            </div>

            <h1
              data-testid="hero-track-title"
              className="mt-6 font-[Unbounded,sans-serif] text-4xl font-semibold uppercase leading-[0.95] tracking-tight text-[#FAFAFA] sm:text-6xl lg:text-7xl"
            >
              BlackBox Radio
            </h1>
            <div className="mt-4 font-mono text-base font-bold uppercase tracking-[0.22em] text-[#A1A1AA]">
              {nowPlaying.title} — {nowPlaying.artist}
            </div>

            <div className="mt-10 flex items-center gap-4">
              <button
                type="button"
                onClick={toggle}
                data-testid="hero-play-button"
                className="group flex items-center gap-3 border border-[#A855F7] bg-[#A855F7] px-6 py-4 text-black transition-colors duration-150 hover:bg-transparent hover:text-[#A855F7]"
              >
                {playing ? <Pause size={18} strokeWidth={2.5} /> : <Play size={18} strokeWidth={2.5} />}
                <span className="font-mono text-base font-bold uppercase tracking-[0.28em]">
                  {playing ? "Pause Stream" : "Tune In"}
                </span>
              </button>
              <a
                href="#schedule"
                data-testid="hero-schedule-link"
                className="border border-white/20 px-6 py-4 font-mono text-base font-bold uppercase tracking-[0.28em] text-[#FAFAFA] transition-colors hover:border-[#FAFAFA] hover:bg-[#FAFAFA] hover:text-black"
              >
                See Schedule
              </a>
            </div>
          </div>

          <div className="md:col-span-5 flex flex-col items-center gap-4 md:self-start md:-mt-14 lg:-mt-20 md:mr-16 lg:mr-24">
            <img
              src="/img/logo.jpg"
              alt="Black Box Music"
              data-testid="hero-logo-image"
              className="w-full max-w-[220px] sm:max-w-[260px] lg:max-w-[300px] select-none"
              style={{ mixBlendMode: "screen" }}
              draggable={false}
            />
            <a
              href="https://pump.fun/coin/CXo4dUwJw6D2m4ELeXZfyNnro4bc8sJJ4WtGcxAipump"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pump-fun-banner-link"
              className="group relative block w-full max-w-[220px] sm:max-w-[240px] lg:max-w-[260px] transition-transform hover:-translate-y-0.5 hover:brightness-110"
              aria-label="Open Black Box Music on pump.fun"
            >
              <img
                src="/img/pump_bbm.png"
                alt="Black Box Music · BBM on pump.fun"
                data-testid="pump-fun-banner-image"
                className="w-full select-none rounded-lg shadow-[0_6px_24px_rgba(168,85,247,0.2)] ring-1 ring-white/10"
                draggable={false}
              />
              {/* Live Market Cap overlay — masks the baked-in "$2.87K" text on the banner */}
              <div
                data-testid="bbm-market-cap-overlay"
                className="pointer-events-none absolute"
                style={{ top: "38%", left: "6.5%", right: "44%", height: "16%" }}
              >
                <div
                  className="flex h-full items-center justify-start rounded-sm px-1 font-[Unbounded,sans-serif] font-bold leading-none text-white"
                  style={{
                    background: "linear-gradient(180deg,#0d1a17 0%,#0a1613 100%)",
                    fontSize: "clamp(14px, 4.2vw, 26px)",
                    letterSpacing: "-0.02em",
                    textShadow: "0 0 12px rgba(168,85,247,0.25)",
                  }}
                >
                  <span data-testid="bbm-market-cap-value">
                    {bbmMc?.formatted || "$—"}
                  </span>
                  {bbmMc?.stale ? (
                    <span
                      data-testid="bbm-market-cap-stale"
                      className="ml-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]"
                    >
                      cached
                    </span>
                  ) : null}
                </div>
              </div>
            </a>
            <button
              type="button"
              onClick={() => setHowToOpen(true)}
              data-testid="bbm-how-to-buy-button"
              aria-label="Open How to Buy $BBM guide"
              className="group inline-flex w-full max-w-[260px] sm:max-w-[280px] lg:max-w-[300px] items-center justify-center gap-2 border border-white/15 bg-black/60 px-4 py-3 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#FAFAFA] backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[#A855F7] hover:text-[#A855F7]"
            >
              <HelpCircle size={14} strokeWidth={2.5} />
              How to buy $BBM
            </button>
            <a
              href="https://pump.fun/coin/CXo4dUwJw6D2m4ELeXZfyNnro4bc8sJJ4WtGcxAipump"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bbm-token-callout"
              className="group block w-full max-w-[260px] sm:max-w-[280px] lg:max-w-[300px] border border-[#A855F7]/60 bg-black/70 p-4 text-center font-[Unbounded,sans-serif] text-sm font-bold leading-snug text-[#FAFAFA] shadow-[0_0_24px_rgba(168,85,247,0.15)] backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-[#A855F7] hover:shadow-[0_0_36px_rgba(168,85,247,0.35)]"
              aria-label="Buy $BBM on pump.fun"
            >
              Don&rsquo;t skip our beat! Own <span className="text-[#A855F7]">$BBM</span> — Power Black Box Music Free Radio, join our movement, spread the word, create generational wealth!
              <span className="mt-3 block font-[Unbounded,sans-serif] text-base font-bold uppercase tracking-[0.28em] text-[#A855F7] transition-colors group-hover:text-white">
                Buy Now →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section id="schedule" className="border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-4 py-20 sm:px-6">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-mono text-base font-bold uppercase tracking-[0.28em] text-[#A855F7]">§ 01</div>
              <h2 className="mt-2 font-[Unbounded,sans-serif] text-3xl uppercase tracking-tight sm:text-4xl">
                Weekly Schedule
              </h2>
            </div>
            <div className="hidden font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A1A1AA] sm:block">
              All times ET // America/New_York
            </div>
          </div>

          {/* Deep Corners Blocks 1-7 header band — replaces the top of the schedule */}
          <BlocksBanner deepCorners={settings.deep_corners} />

          <div className="mt-10 grid grid-cols-1 gap-px bg-white/5 md:grid-cols-7">
            {DAYS.map((day) => (
              <div key={day} className="min-h-[220px] bg-black/55 backdrop-blur-sm p-4" data-testid={`schedule-day-${day.toLowerCase()}`}>
                <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2">
                  <span className="font-[Unbounded,sans-serif] text-sm font-bold uppercase tracking-[0.22em] text-[#FAFAFA]">
                    {day}
                  </span>
                  <span className="font-mono text-sm font-bold text-[#A1A1AA]">{showsByDay[day].length}</span>
                </div>
                <div className="space-y-3">
                  {showsByDay[day].length === 0 ? (
                    <div className="font-mono text-sm font-bold text-[#A1A1AA]">— Off Air</div>
                  ) : (
                    showsByDay[day].map((show) => (
                      <div key={show.id} className="group cursor-default">
                        <div className="flex items-center gap-1 font-mono text-sm font-bold uppercase tracking-[0.14em] text-[#A855F7]">
                          <Clock size={10} />
                          {show.start_time} — {show.end_time}
                        </div>
                        <div className="mt-1 font-[Unbounded,sans-serif] text-base font-bold text-[#FAFAFA] group-hover:text-[#A855F7]">
                          {show.title}
                        </div>
                        <div className="font-mono text-sm font-bold text-[#FAFAFA]">{show.dj}</div>
                        {show.genre ? (
                          <div className="mt-1 font-mono text-xs font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                            {show.genre}
                          </div>
                        ) : null}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Black Box Music On Demand — access to the full uploaded library */}
      <section id="on-demand-teaser" className="border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6">
          <Link
            to="/on-demand"
            data-testid="on-demand-card"
            aria-label="Open Black Box Music On Demand"
            className="group relative flex flex-col items-center gap-6 border border-[#A855F7]/50 bg-black/70 p-8 backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-[#A855F7] hover:bg-black/80 hover:shadow-[0_0_44px_rgba(168,85,247,0.35)] sm:flex-row sm:gap-10 sm:p-10"
          >
            <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center overflow-hidden border border-white/10 bg-black sm:h-36 sm:w-36">
              <img
                src="/img/logo.jpg"
                alt="Black Box Music"
                className="h-full w-full object-contain"
                style={{ mixBlendMode: "screen" }}
                draggable={false}
              />
            </div>
            <div className="flex flex-1 flex-col text-center sm:text-left">
              <div className="font-mono text-xs font-bold uppercase tracking-[0.28em] text-[#A855F7]">
                Play the full catalog
              </div>
              <div className="mt-2 font-[Unbounded,sans-serif] text-3xl font-bold uppercase leading-tight tracking-tight text-[#FAFAFA] sm:text-4xl">
                Black Box Music
                <span className="ml-2 text-[#A855F7]">On Demand</span>
              </div>
              <p className="mt-3 max-w-2xl font-mono text-sm font-bold leading-relaxed text-[#A1A1AA] sm:text-base">
                Every uploaded Black Box Music track, playing in constant rotation. Skip freely, shuffle, or let it run.
              </p>
              <span className="mt-6 inline-flex items-center justify-center gap-2 self-center border border-[#A855F7] bg-[#A855F7] px-5 py-3 font-[Unbounded,sans-serif] text-sm font-bold uppercase tracking-[0.28em] text-black transition-colors group-hover:bg-transparent group-hover:text-[#A855F7] sm:self-start">
                <Play size={14} strokeWidth={2.5} /> Enter Player
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Featured Tracks — only artists with a dedicated detail page are shown */}
      <section id="featured" className="border-b border-white/5">
        <div className="mx-auto max-w-[1400px] px-4 py-20 sm:px-6">
          <div className="flex items-end justify-between">
            <div>
              <div className="font-mono text-base font-bold uppercase tracking-[0.28em] text-[#A855F7]">§ 03</div>
              <h2 className="mt-2 font-[Unbounded,sans-serif] text-3xl uppercase tracking-tight sm:text-4xl">
                Black Box Music Featured Artist
              </h2>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-px bg-white/5 sm:grid-cols-2 lg:grid-cols-3">
            {tracks
              .filter((t) => Boolean(artistSlug(t.artist)))
              .map((t) => {
                const slug = artistSlug(t.artist);
                return (
                  <Link
                    key={t.id}
                    to={`/artist/${slug}`}
                    data-testid={`featured-track-${t.id}`}
                    className="group relative flex flex-col bg-black/55 backdrop-blur-sm transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A855F7]"
                    aria-label={`Open ${t.artist} artist page`}
                  >
                    <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "4 / 5" }}>
                      {t.cover_url ? (
                        <img
                          src={t.cover_url}
                          alt=""
                          className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-[#52525B]">
                          <Radio size={40} />
                        </div>
                      )}
                      <div className="pointer-events-none absolute right-3 top-3 border border-[#A855F7]/70 bg-black/70 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#A855F7] backdrop-blur-sm">
                        Open Page →
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 p-5">
                      <div className="font-[Unbounded,sans-serif] text-lg font-bold text-[#FAFAFA] transition-colors group-hover:text-[#A855F7]">
                        {t.title}
                      </div>
                      <div className="font-mono text-sm font-bold uppercase tracking-[0.18em] text-[#FAFAFA]">{t.artist}</div>
                      <div className="mt-1 flex items-center gap-3 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">
                        <span>{t.genre}</span>
                        {t.duration ? <span>// {t.duration}</span> : null}
                      </div>
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* Overnight healing frequency picker */}
      <FrequencyPicker />

      {/* About */}
      <section id="about" className="border-b border-white/5">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-4 py-20 sm:px-6 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="font-mono text-base font-bold uppercase tracking-[0.28em] text-[#A855F7]">§ 04</div>
            <h2 className="mt-2 font-[Unbounded,sans-serif] text-3xl uppercase tracking-tight sm:text-4xl">
              {settings.about_title || "Black Box Music"}
            </h2>
          </div>
          <div className="md:col-span-8">
            <p className="font-mono text-lg font-semibold leading-relaxed text-[#FAFAFA]">
              {settings.about_body ||
                "An underground radio station broadcasting handpicked electronic, ambient, and experimental sounds from around the globe. Tuned in 24/7."}
            </p>
            <div className="mt-10 grid grid-cols-2 gap-8 md:grid-cols-4">
              {[
                { k: "Shows / Week", v: shows.length },
                { k: "Hours Live", v: "24/7" },
                { k: "Resident DJs", v: new Set(shows.map((s) => s.dj)).size },
                { k: "Est.", v: "2026" },
              ].map((it) => (
                <div key={it.k} className="border-t border-white/10 pt-4">
                  <div className="font-[Unbounded,sans-serif] text-2xl text-[#FAFAFA]">{it.v}</div>
                  <div className="mt-1 font-mono text-sm font-bold uppercase tracking-[0.22em] text-[#A855F7]">{it.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer settings={settings} />
      <BuyBBMOnboardingModal open={howToOpen} onOpenChange={setHowToOpen} />
    </div>
  );
};

export default Home;
